import Component from '@glimmer/component';
import { computed, action, set } from '@ember/object';
import { tracked } from '@glimmer/tracking';

import FunctionalUtils from 'mewe/shared/functional-utils';
import { inject as service } from '@ember/service';
import isUndefined from 'mewe/utils/isUndefined';
import isDefined from 'mewe/utils/isDefined';
import AccountApi from 'mewe/api/account-api';
import { ds } from 'mewe/stores/ds';

export default class MwSettingsBadges extends Component {
  @service account;
  @service analytics;

  @tracked badges = {};
  @tracked selectedBadges = [];
  @tracked questState = ds.questState;

  constructor() {
    super(...arguments);

    AccountApi.getBadges().then((data) => {
      // carry the value of ambassador badge because it's not returned by getBadges endpoint
      data.ambassador = this.account.activeUser.badges.ambassador;

      set(this, `account.activeUser.badges`, data);
      this.badges = this.account.activeUser.badges;
      this.updateBadges();
    });
  }

  @computed('badges.verified', 'selectedBadges.length')
  get isVerifiedBadgeDisabled() {
    return isUndefined(this.badges.verified) || (this.selectedBadges.length >= 2 && !this.badges.verified);
  }

  @computed('badges.premium', 'selectedBadges.length')
  get isPremiumBadgeDisabled() {
    return isUndefined(this.badges?.premium) || (this.selectedBadges.length >= 2 && !this.badges.premium);
  }

  @computed('badges.donor', 'selectedBadges.length')
  get isDonorBadgeDisabled() {
    return isUndefined(this.badges?.donor) || (this.selectedBadges.length >= 2 && !this.badges.donor);
  }

  @computed('badges.level1', 'selectedBadges.length')
  get isQuestBadgeDisabled() {
    return !this.levelBadgeNumber || (this.selectedBadges.length >= 2 && !this.badges[this.levelBadgeName]);
  }

  @computed('questState.isLoaded', 'selectedBadges.length')
  get levelBadgeNumber() {
    if (this.questState.isLoaded) {
      // return higherst existing level badge
      if (isDefined(this.badges?.level4)) return 4;
      if (isDefined(this.badges?.level3)) return 3;
      if (isDefined(this.badges?.level2)) return 2;
      if (isDefined(this.badges?.level1)) return 1;
    }
    return null;
  }

  get levelBadgeName() {
    return `level${this.levelBadgeNumber}`;
  }

  updateBadges(name, value) {
    if (name) {
      set(this, `badges.${name}`, value);
    }

    let selected = [];
    for (const key in this.badges) {
      // Exclude ambassador badge from selectedBadges count as it's not user-selectable
      if (this.badges[key] && key !== 'ambassador') {
        selected.push(key);
      }
    }

    this.selectedBadges = selected;
  }

  @action
  setBadge(name) {
    const newValue = !this.badges[name];

    this.updateBadges(name, newValue);

    AccountApi.setBadges({ badges: this.selectedBadges })
      .then(() => {
        FunctionalUtils.info(__('Changes in your account have been saved'));
        set(this, `account.activeUser.badges.${name}`, newValue);
      })
      .catch(() => {
        FunctionalUtils.showDefaultErrorMessage();
        this.updateBadges(name, !newValue);
      });
  }

  @action
  levelBadgeClicked() {
    this.analytics.sendEvent('learnMoreClicked', { location: 'badge settings', element: 'level badge' });
  }
}
